import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import { useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import CardActionArea from '@mui/material/CardActionArea';
import Box from '@mui/material/Box';
import {
  TextV2,
  SvgMultipleUsers2Bold,
  Icon,
} from '@insights-ltd/design-library/components';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';

type AddLearnersCardProps = {
  noOfInvitees: number;
  disabled: boolean;
  readonly: boolean;
  learnerActionsCount: number;
};

const cardTitle = (
  readonly: boolean,
  noOfInvitees: number,
  t: (key: string) => string,
) => {
  if (readonly) {
    return t('ui.event-management.events.summary.view-learners');
  }
  if (noOfInvitees === 0) {
    return t('ui.event-management.events.summary.add-learners');
  }

  return t('ui.event-management.events.summary.manage-learners');
};

const AddLearnersCard = ({
  noOfInvitees,
  disabled,
  readonly,
  learnerActionsCount,
}: AddLearnersCardProps) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const isDisabled = disabled && !readonly;

  const learnersTitle = cardTitle(readonly, noOfInvitees, t);

  return (
    <Card
      elevation={0}
      sx={(theme) => ({
        height: '100%',
        border: isDisabled ? '0' : `1px solid ${theme.palette.grey[500]}`,
        borderBottom: isDisabled
          ? 'undefined'
          : `0.2em solid ${theme.palette.primary.main}`,
        opacity: isDisabled ? '0.5' : 'undefined',
      })}
    >
      <CardActionArea
        disabled={isDisabled}
        onClick={() => navigate(`${pathname}/learners`)}
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
          width: '100%',
          height: '100%',
          fontFamily: 'inherit',
        }}
        aria-labelledby="add-learners-title"
      >
        <CardContent>
          <Icon
            icon={<SvgMultipleUsers2Bold />}
            iconColour="green"
            iconShape="square"
          />
          <Box
            sx={{
              mb: (theme) => theme.spacing(spacingSizeMap.S),
              mt: (theme) => theme.spacing(spacingSizeMap.S),
            }}
          >
            <TextV2
              component="p"
              id="add-learners-title"
              variant="h3"
              color={isDisabled ? 'textPrimary' : 'primary'}
            >
              {learnersTitle}
            </TextV2>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <TextV2 variant="bodyBold" color="textSecondary">
              {noOfInvitees === 0
                ? t('ui.event-management.events.summary.get-started')
                : t(
                    'ui.event-management.events.summary.overview.manage-learners.no-of-learners',
                    {
                      count: noOfInvitees,
                    },
                  )}
            </TextV2>
            {learnerActionsCount > 0 && (
              <TextV2 variant="bodyBold" color="error">
                {t(
                  'ui.event-management.events.summary.manage-learners-actions-message',
                  { count: learnerActionsCount },
                )}
              </TextV2>
            )}
          </Box>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

export default AddLearnersCard;
